import React, { useState } from 'react';
import { X, Plus, Trash2, Save, Loader2 } from 'lucide-react';
import { createFAQ } from '../services/faqService';

/**
 * AddToFAQModal Component
 * 
 * Modal for admins to add Q&A pairs to the FAQ system
 * Supports multiple alternative phrasings of the same question
 */
const AddToFAQModal = ({ question, answer, sources, onClose, onSuccess }) => {
  const [primaryQuestion, setPrimaryQuestion] = useState(question || '');
  const [alternativeQuestions, setAlternativeQuestions] = useState(['']);
  const [faqAnswer, setFaqAnswer] = useState(answer || '');
  const [category, setCategory] = useState('general');
  const [tags, setTags] = useState('');
  const [difficulty, setDifficulty] = useState('intermediate');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  // Add a new alternative question input
  const addAlternativeQuestion = () => {
    setAlternativeQuestions([...alternativeQuestions, '']);
  };

  // Update an alternative question
  const updateAlternativeQuestion = (index, value) => {
    const updated = [...alternativeQuestions];
    updated[index] = value;
    setAlternativeQuestions(updated);
  };

  // Remove an alternative question
  const removeAlternativeQuestion = (index) => {
    const updated = alternativeQuestions.filter((_, i) => i !== index);
    setAlternativeQuestions(updated);
  };

  // Save FAQ to database
  const handleSave = async () => {
    // Validation
    if (!primaryQuestion.trim()) {
      setError('Primary question is required');
      return;
    }
    if (!faqAnswer.trim()) {
      setError('Answer is required');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      // Filter out empty alternative questions
      const filteredAlternatives = alternativeQuestions
        .map(q => q.trim())
        .filter(q => q.length > 0);

      // Prepare FAQ data
      const faqData = {
        primaryQuestion: primaryQuestion.trim(),
        alternativeQuestions: filteredAlternatives,
        answer: faqAnswer.trim(),
        sources: sources || [],
        category,
        tags: tags.split(',').map(t => t.trim()).filter(t => t),
        difficulty,
        status: 'active' // Immediately active since admin is creating it
      };

      console.log('💾 Creating FAQ:', faqData);

      // Call API
      const result = await createFAQ(faqData);

      if (result.success) {
        console.log('✅ FAQ created successfully:', result.faq);
        if (onSuccess) {
          onSuccess(result.faq);
        }
        onClose();
      } else {
        setError(result.message || 'Failed to create FAQ');
      }
    } catch (err) {
      console.error('❌ Error creating FAQ:', err);
      setError(err.message || 'An error occurred while saving the FAQ');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-pvs-card border border-pvs-border rounded-xl shadow-pvs-glow max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-pvs-border">
          <h2 className="text-xl font-bold text-white">Add to FAQ</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-pvs-card-light rounded transition-colors"
            title="Close"
          >
            <X className="w-5 h-5 text-pvs-text-dim" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Error Message */}
          {error && (
            <div className="bg-red-900/30 border border-red-500/30 rounded-lg p-3">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {/* Info Box */}
          <div className="bg-pvs-accent/5 border border-pvs-accent/20 rounded-lg p-3">
            <p className="text-sm text-pvs-accent">
              <strong>💡 Tip:</strong> Add multiple versions of the same question (e.g., "What is X?", "Define X", "Tell me about X") 
              so users can ask in different ways and get the same answer instantly!
            </p>
          </div>

          {/* Primary Question */}
          <div>
            <label className="block text-sm font-semibold text-pvs-text-muted mb-1">
              Primary Question <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={primaryQuestion}
              onChange={(e) => setPrimaryQuestion(e.target.value)}
              className="w-full px-3 py-2 bg-pvs-input-bg border border-pvs-input-border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-pvs-accent/50 focus:border-pvs-accent/50 placeholder-pvs-text-dim"
              placeholder="e.g., What's the maximum span for 32mm steel pipework?"
            />
          </div>

          {/* Alternative Questions */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-semibold text-pvs-text-muted">
                Alternative Phrasings (Optional)
              </label>
              <button
                onClick={addAlternativeQuestion}
                className="flex items-center gap-1 text-sm text-pvs-accent hover:text-pvs-accent-dim transition-colors font-semibold"
              >
                <Plus className="w-4 h-4" />
                Add Variation
              </button>
            </div>
            <div className="bg-amber-900/20 border border-amber-500/30 rounded px-2 py-1 mb-2">
              <p className="text-xs text-amber-400">
                ⚠️ <strong>Important:</strong> Click "Add Variation" for EACH alternative question. 
                Do NOT put multiple questions in one field separated by quotes!
              </p>
            </div>
            <p className="text-xs text-pvs-text-muted mb-2">
              Add different ways users might ask the same question
            </p>
            <div className="space-y-2">
              {alternativeQuestions.map((alt, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={alt}
                    onChange={(e) => updateAlternativeQuestion(index, e.target.value)}
                    className="flex-1 px-3 py-2 bg-pvs-input-bg border border-pvs-input-border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-pvs-accent/50 focus:border-pvs-accent/50 placeholder-pvs-text-dim"
                    placeholder={`e.g., What is the max pipe support distance for 32mm?`}
                  />
                  <button
                    onClick={() => removeAlternativeQuestion(index)}
                    className="p-2 hover:bg-red-900/30 text-red-400 rounded-lg transition-colors"
                    title="Remove"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Answer */}
          <div>
            <label className="block text-sm font-semibold text-pvs-text-muted mb-1">
              Answer <span className="text-red-400">*</span>
            </label>
            <textarea
              value={faqAnswer}
              onChange={(e) => setFaqAnswer(e.target.value)}
              rows={6}
              className="w-full px-3 py-2 bg-pvs-input-bg border border-pvs-input-border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-pvs-accent/50 focus:border-pvs-accent/50 placeholder-pvs-text-dim resize-none"
              placeholder="The answer from VesselMind..."
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-semibold text-pvs-text-muted mb-1">
              Category
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2 bg-pvs-input-bg border border-pvs-input-border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-pvs-accent/50 focus:border-pvs-accent/50"
            >
              <option value="general">General</option>
              <option value="pipework">Pipework & Supports</option>
              <option value="vessels">Vessels & Tanks</option>
              <option value="separation">Separation Distances</option>
              <option value="installation">Installation</option>
              <option value="inspection">Inspection & Testing</option>
              <option value="bulk">Bulk Storage</option>
              <option value="safety">Safety & Emergency</option>
              <option value="automotive">Automotive/Autogas</option>
            </select>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-semibold text-pvs-text-muted mb-1">
              Tags (Optional)
            </label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              className="w-full px-3 py-2 bg-pvs-input-bg border border-pvs-input-border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-pvs-accent/50 focus:border-pvs-accent/50 placeholder-pvs-text-dim"
              placeholder="e.g., steel, pipework, 32mm (comma-separated)"
            />
          </div>

          {/* Difficulty */}
          <div>
            <label className="block text-sm font-semibold text-pvs-text-muted mb-1">
              Difficulty Level
            </label>
            <select
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value)}
              className="w-full px-3 py-2 bg-pvs-input-bg border border-pvs-input-border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-pvs-accent/50 focus:border-pvs-accent/50"
            >
              <option value="basic">Basic</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>

          {/* Sources (Read-only) */}
          {sources && sources.length > 0 && (
            <div>
              <label className="block text-sm font-semibold text-pvs-text-muted mb-1">
                Sources
              </label>
              <div className="bg-pvs-card-light border border-pvs-border rounded-lg p-3">
                <p className="text-sm text-pvs-text-muted">
                  {sources.join(', ')}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-pvs-border bg-pvs-card-light">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 text-pvs-text-muted hover:bg-pvs-card rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-2 px-4 py-2 bg-pvs-accent text-pvs-bg font-semibold rounded-lg hover:bg-pvs-accent-dim transition-colors disabled:opacity-50"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save FAQ
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddToFAQModal;
